import * as React from "react"
import { Link } from "gatsby"
import loadable from "@loadable/component"
import { FiMenu } from "react-icons/fi"
import * as ui from "gatsby-ui"
import { useMatchMedia } from "../../components/shared/hooks"

import Banner from "./banner"
import MetaNav from "./meta-nav"
import Logo from "./logo"
import Dropdown, { DropdownProps } from "./dropdown"
// import Search from "./search"
import * as styles from "./styles/index.css"

import { SubNavProps } from "./subnav"

// lazy components
const OpenSourceMenu = loadable(() => import("./open-source-menu"))
const LearnMenu = loadable(() => import("./learn-menu"))
const MobileMenu = loadable(() => import("./mobile-menu"))
const Subnav = loadable(() => import("./subnav"))

const navItems: DropdownProps[] = [
  { name: "Open Source", menu: OpenSourceMenu },
  { name: "Learn", menu: LearnMenu },
]

export const join = (...args: Array<string | boolean>) =>
  args.filter(Boolean).join(" ")

// testing idle until urgent
// callOnce boolean?
// consider task-only version?
export const useIdleUntilUrgent = (fn: () => void) => {
  const [value, setValue] = React.useState<void | undefined>(undefined)
  const [called, setCalled] = React.useState(false)

  let getValue: VoidFunction = () => ({})
  let callback: number | undefined

  React.useEffect(() => {
    if (!called) {
      if (typeof window.requestIdleCallback !== "undefined") {
        callback = requestIdleCallback(() => {
          setValue(fn())
        })
      } else {
        setTimeout(() => {
          setValue(fn())
        })
      }
      setCalled(true)
    }

    getValue = () => {
      if (callback && !called && value === undefined) {
        cancelIdleCallback(callback)
        setValue(fn())
      }
      return value
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return getValue
}

export interface HeaderProps {
  inverted?: boolean
  subnav?: SubNavProps
  withBanner?: boolean
  setWithBanner?: (val: boolean) => void
}

export default function Header({
  inverted = false,
  subnav,
  withBanner = false,
  setWithBanner,
}: HeaderProps) {
  // mobile nav
  const [open, setOpen] = React.useState(false)
  const isMobile = useMatchMedia(ui.media.mobileOnly)

  React.useEffect(() => {
    if (!isMobile) {
      setOpen(false)
    }
  }, [isMobile])

  React.useEffect(() => {
    if (open) {
      document.body.style.position = "fixed"
      document.body.style.width = "100%"
    } else {
      document.body.style.position = ""
      document.body.style.width = ""
    }
  }, [open])

  const mobileMenuOpen = open && isMobile

  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setOpen(!open)
  }

  const preload = useIdleUntilUrgent(() => {
    if (isMobile) return
    OpenSourceMenu.preload()
    LearnMenu.preload()
  })

  useIdleUntilUrgent(() => {
    if (!isMobile) return
    MobileMenu.preload()
  })

  return (
    <>
      <div
        className={join(
          inverted ? styles.bannerInverted : styles.banner,
          withBanner && styles.bannerSticky
        )}
      >
        <div className={styles.bannerContainer}>
          <Banner onDataLoad={() => setWithBanner && setWithBanner(true)} />
          <MetaNav />
        </div>
      </div>
      <header
        onMouseOver={preload}
        onFocus={preload}
        className={join(
          inverted ? styles.headerInverted : styles.header,
          !subnav && styles.headerSticky,
          !subnav && withBanner && styles.headerStickyShifted
        )}
      >
        <div className={styles.headerContainer}>
          <Link to="/" aria-label="Link to home" className={styles.logoLink}>
            <Logo inverted={inverted} width={91} height={24} />
          </Link>
          <nav className={styles.mainNavItems}>
            {navItems.map(item => (
              <Dropdown key={item.name} {...item} />
            ))}
          </nav>
          {!subnav && (
            <>
              {/* <Search /> */}
              <Link className={styles.contactLink} to="/contact-us">
                Contact
              </Link>
              <Link
                className={styles.ctaLink}
                to="https://app.netlify.com/signup"
              >
                Sign Up
              </Link>
            </>
          )}
          <button
            onClick={toggleMenu}
            className={join(styles.menuButton, subnav && styles.marginLeftAuto)}
          >
            <FiMenu />
            <span className={styles.hide}>Menu</span>
          </button>
        </div>
      </header>
      {subnav && <Subnav {...subnav} withBanner={withBanner} />}
      {mobileMenuOpen && <MobileMenu onClose={() => setOpen(false)} />}
    </>
  )
}
